/**
 * Leaders Chat Service
 *
 * Core service for the encrypted Leaders Chat channel. Provides all Firestore
 * operations, real-time subscriptions, typing indicators, and push notification
 * support. Encryption uses the same AES-256-GCM / HKDF pattern as DMs, with a
 * static channel ID for key derivation.
 *
 * Only users with role 'leader' or 'admin' can access this channel.
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  increment,
  type DocumentReference,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import {
  encryptionService,
  DECRYPTING_PLACEHOLDER,
  DECRYPT_FAILED_PLACEHOLDER,
  type EncryptedEnvelope,
} from './encryption';
import {
  LEADERS_CHAT_CHANNEL_ID,
  type LeadersChatMessage,
  type LeadersChatMessageInput,
  type LeadersChatUserMetadata,
} from '@/types/leadersChat';
import { createMentionPushNotifications } from './mentions';
import { mentionToPlainText } from '@/utils/mentionParser';
import type { MentionData } from '@/types/mentions';
import type { EmbeddedContent } from './community';
import type { LibraryFile } from '@/types';

// ==================== Collection Path Helpers ====================
// Firestore collection paths must have an odd number of segments.
// We use "leaders_chat/channel/messages" (5 segments) — the "channel"
// document acts as a container, mirroring the "public/data/messages" pattern.

const getLeadersChatMessagesPath = () => `artifacts/${appId}/leaders_chat/channel/messages`;

const getLeadersChatTypingPath = (userId: string) =>
  `artifacts/${appId}/leaders_chat/channel/typing/${userId}`;

const getLeadersChatTypingCollectionPath = () => `artifacts/${appId}/leaders_chat/channel/typing`;

const getLeadersChatChannelDocPath = () => `artifacts/${appId}/leaders_chat/channel`;

const getUserLeadersChatMetadataPath = (userId: string) =>
  `artifacts/${appId}/users/${userId}/leaders_chat_metadata`;

const getPushQueuePath = () => `artifacts/${appId}/push_queue`;

// ==================== Push Notification Helpers ====================

/**
 * Create a push notification entry for a Leaders Chat message.
 * Encrypts the preview for at-rest protection in the push_queue.
 */
async function createLeadersChatPushNotification(data: {
  senderId: string;
  senderName: string;
  messageId: string;
  preview: string;
  recipientIds: string[];
}): Promise<void> {
  const pushQueueRef = collection(db, getPushQueuePath());

  // Create a push queue entry for each recipient
  const promises = data.recipientIds.map(async (recipientId) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const queueData: Record<string, any> = {
      type: 'leaders_chat',
      recipientId,
      title: `Leaders Chat - ${data.senderName}`,
      body: 'New message in Leaders Chat',
      data: {
        messageId: data.messageId,
        senderId: data.senderId,
        url: '/chat/leaders',
      },
      createdAt: serverTimestamp(),
    };

    // Encrypt the preview for at-rest protection
    if (encryptionService.isReady() && data.preview) {
      try {
        const encrypted = await encryptionService.encryptPreview(
          LEADERS_CHAT_CHANNEL_ID,
          data.preview
        );
        queueData.encryptedBody = encrypted.ciphertext;
        queueData.bodyIv = encrypted.iv;
        queueData.bodyKeyVersion = encrypted.keyVersion;
        queueData.conversationId = LEADERS_CHAT_CHANNEL_ID;
      } catch {
        // Fall through to use generic "New message" body
      }
    }

    await addDoc(pushQueueRef, queueData);
  });

  await Promise.all(promises);
}

// ==================== Message Operations ====================

type EncryptedMessageResult = {
  ciphertext: string;
  iv: string;
  keyVersion: number;
};

function buildMessageDocument(
  senderId: string,
  senderName: string,
  senderRole: string,
  encryptedMessage: EncryptedMessageResult,
  input: LeadersChatMessageInput
): Record<string, unknown> {
  const messageData: Record<string, unknown> = {
    senderId,
    senderName,
    senderRole,
    timestamp: serverTimestamp(),
    isDeleted: false,
    encrypted: true,
    ciphertext: encryptedMessage.ciphertext,
    iv: encryptedMessage.iv,
    keyVersion: encryptedMessage.keyVersion,
    hasAttachments: (input.attachments?.length ?? 0) > 0,
    ...(input.clientId && { clientId: input.clientId }),
  };
  if (input.embeddedContent) messageData.embeddedContent = input.embeddedContent;
  if (input.mentionedUserIds && input.mentionedUserIds.length > 0) {
    messageData.mentionedUserIds = input.mentionedUserIds;
  }
  return messageData;
}

async function updateChannelMetadata(
  channelRef: DocumentReference,
  senderId: string,
  senderName: string
): Promise<void> {
  try {
    await setDoc(
      channelRef,
      {
        lastMessageAt: serverTimestamp(),
        lastMessageBy: senderName,
        lastMessageSenderId: senderId,
      },
      { merge: true }
    );
  } catch {
    // Non-critical
  }
}

function notifyMentionedUsers(
  messageId: string,
  senderId: string,
  senderName: string,
  input: LeadersChatMessageInput
): void {
  if (!input.mentionedUserIds || input.mentionedUserIds.length === 0) return;
  const mentionRoles: Record<string, import('@/types').UserRole> = {};
  if (input.mentions) {
    for (const m of input.mentions) {
      if (m.role) mentionRoles[m.userId] = m.role;
    }
  }
  createMentionPushNotifications({
    senderId,
    senderName,
    messageId,
    mentionedUserIds: input.mentionedUserIds,
    mentionedUserRoles: Object.keys(mentionRoles).length > 0 ? mentionRoles : undefined,
    chatType: 'leaders',
  }).catch((error) =>
    console.error('[LeadersChat] Failed to create mention notifications:', error)
  );
}

export async function sendLeadersChatMessage(
  senderId: string,
  senderName: string,
  senderRole: string,
  input: LeadersChatMessageInput
): Promise<string> {
  if (!encryptionService.isReady()) {
    throw new Error('Encryption service not ready. Please wait and try again.');
  }

  const plainPreview = mentionToPlainText(input.text);
  const preview = plainPreview.substring(0, 100) + (plainPreview.length > 100 ? '...' : '');

  const envelope: EncryptedEnvelope = {
    text: input.text,
    attachments: input.attachments || [],
    ...(input.quotedMessageId && { quotedMessageId: input.quotedMessageId }),
    ...(input.quotedMessage && { quotedMessage: input.quotedMessage }),
    ...(input.mentions && input.mentions.length > 0 && { mentions: input.mentions }),
  };

  const encryptedMessage = await encryptionService.encryptMessage(
    LEADERS_CHAT_CHANNEL_ID,
    envelope
  );

  const messageData = buildMessageDocument(
    senderId,
    senderName,
    senderRole,
    encryptedMessage,
    input
  );

  const messagesRef = collection(db, getLeadersChatMessagesPath());
  const messageRef = await addDoc(messagesRef, messageData);

  const metadataRef = doc(db, getLeadersChatChannelDocPath());
  await updateChannelMetadata(metadataRef, senderId, senderName);

  incrementUnreadForOthers(senderId, messageRef.id, senderName, preview).catch((error) =>
    console.error('[LeadersChat] Failed to update unread counts:', error)
  );

  notifyMentionedUsers(messageRef.id, senderId, senderName, input);

  return messageRef.id;
}

/**
 * Increment unread count for all leaders/admins except the sender,
 * and create push notifications.
 */
async function incrementUnreadForOthers(
  senderId: string,
  messageId: string,
  senderName: string,
  preview: string
): Promise<void> {
  // Query all users who have leaders_chat_metadata (active leaders/admins)
  // We'll iterate through them and increment their unread count
  const usersRef = collection(db, `artifacts/${appId}/users`);
  const usersSnapshot = await getDocs(usersRef);

  const recipientIds: string[] = [];

  const updatePromises = usersSnapshot.docs.map(async (userDoc) => {
    const userId = userDoc.id;
    if (userId === senderId) return;

    // Check if user has leaders_chat_metadata (is a leader/admin with access)
    const metaRef = doc(db, getUserLeadersChatMetadataPath(userId), 'info');
    const metaSnap = await getDoc(metaRef);

    if (metaSnap.exists()) {
      const metadata = metaSnap.data() as LeadersChatUserMetadata;
      if (!metadata.isMuted) {
        await setDoc(metaRef, { unreadCount: increment(1) }, { merge: true });
        recipientIds.push(userId);
      }
    }
  });

  await Promise.all(updatePromises);

  // Create push notifications for all non-muted recipients
  if (recipientIds.length > 0) {
    try {
      await createLeadersChatPushNotification({
        senderId,
        senderName,
        messageId,
        preview,
        recipientIds,
      });
    } catch (error) {
      console.error('[LeadersChat] Failed to create push notifications:', error);
    }
  }
}

/**
 * Soft delete a message (mark as deleted, clear encrypted content).
 * Admins can delete any message; leaders can only delete their own.
 */
export async function deleteLeadersChatMessage(
  messageId: string,
  userId: string,
  isAdmin: boolean
): Promise<void> {
  const messageRef = doc(db, getLeadersChatMessagesPath(), messageId);
  const messageSnap = await getDoc(messageRef);

  if (!messageSnap.exists()) {
    throw new Error('Message not found');
  }

  const data = messageSnap.data();

  // Permission check: admins can delete any, leaders only their own
  if (!isAdmin && data.senderId !== userId) {
    throw new Error('You can only delete your own messages');
  }

  await updateDoc(messageRef, {
    isDeleted: true,
    text: '[Message deleted]',
    ciphertext: null,
    iv: null,
    keyVersion: null,
    hasAttachments: false,
    embeddedContent: null,
  });
}

/**
 * Pin or unpin a message in Leaders Chat
 */
export async function pinLeadersChatMessage(
  messageId: string,
  userId: string,
  isPinned: boolean
): Promise<void> {
  const messageRef = doc(db, getLeadersChatMessagesPath(), messageId);
  await updateDoc(messageRef, {
    isPinned: !isPinned,
    pinnedAt: !isPinned ? serverTimestamp() : null,
    pinnedBy: !isPinned ? userId : null,
  });
}

// ==================== Subscriptions (Real-time) ====================

/**
 * Subscribe to Leaders Chat messages with dual-read decryption.
 * Encrypted messages show placeholders initially, then are decrypted in background.
 */
export function subscribeToLeadersChatMessages(
  callback: (messages: LeadersChatMessage[]) => void
): () => void {
  const messagesRef = collection(db, getLeadersChatMessagesPath());
  const q = query(messagesRef, orderBy('timestamp', 'asc'));

  let latestSnapshotId = 0;

  return onSnapshot(
    q,
    (snapshot) => {
      const currentSnapshotId = ++latestSnapshotId;

      // Build raw messages with placeholders for encrypted ones
      const rawMessages = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          // For encrypted messages, show placeholder until decrypted
          text: data.encrypted && !data.isDeleted ? DECRYPTING_PLACEHOLDER : data.text || '',
          attachments: data.encrypted && !data.isDeleted ? [] : data.attachments || [],
        } as LeadersChatMessage;
      });

      // Immediately provide raw messages so UI is responsive
      callback(rawMessages);

      // Check if any messages need decryption
      const hasEncrypted = snapshot.docs.some((d) => d.data().encrypted && !d.data().isDeleted);
      if (!hasEncrypted || !encryptionService.isReady()) return;

      // Decrypt encrypted messages in background
      Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();

          // Pass through unencrypted and deleted messages
          if (!data.encrypted || data.isDeleted) {
            return { id: docSnap.id, ...data } as LeadersChatMessage;
          }

          try {
            const envelope = await encryptionService.decryptMessage(LEADERS_CHAT_CHANNEL_ID, {
              ciphertext: data.ciphertext,
              iv: data.iv,
              keyVersion: data.keyVersion,
            });

            return {
              id: docSnap.id,
              ...data,
              text: envelope.text,
              attachments: envelope.attachments || [],
              quotedMessageId: envelope.quotedMessageId || data.quotedMessageId,
              quotedMessage: envelope.quotedMessage || data.quotedMessage,
              // Restore mention data from encrypted envelope
              mentions:
                (envelope as EncryptedEnvelope & { mentions?: MentionData[] }).mentions ||
                undefined,
            } as LeadersChatMessage;
          } catch (error) {
            console.error(`[LeadersChat] Decryption failed for message ${docSnap.id}:`, error);
            return {
              id: docSnap.id,
              ...data,
              text: DECRYPT_FAILED_PLACEHOLDER,
              attachments: [] as LeadersChatMessage['attachments'],
            } as unknown as LeadersChatMessage;
          }
        })
      )
        .then((decryptedMessages) => {
          if (currentSnapshotId === latestSnapshotId) {
            callback(decryptedMessages);
          }
        })
        .catch((error) => {
          console.error('[LeadersChat] Background decryption failed:', error);
        });
    },
    (error) => {
      console.error('[LeadersChat] Message subscription error:', error);
    }
  );
}

/**
 * Subscribe to typing indicators in Leaders Chat
 */
export function subscribeToLeadersChatTyping(
  currentUserId: string,
  callback: (typingUsers: { userId: string; userName: string }[]) => void
): () => void {
  const typingRef = collection(db, getLeadersChatTypingCollectionPath());

  return onSnapshot(typingRef, (snapshot) => {
    const typingUsers: { userId: string; userName: string }[] = [];

    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      // Exclude current user, check recency (5s staleness)
      if (docSnap.id === currentUserId) return;

      const typingTimestamp = data.timestamp as Timestamp;
      const isRecent = typingTimestamp && Date.now() - typingTimestamp.toMillis() < 5000;

      if (data.isTyping && isRecent) {
        typingUsers.push({
          userId: docSnap.id,
          userName: data.userName || 'Someone',
        });
      }
    });

    callback(typingUsers);
  });
}

/**
 * Set typing status for current user in Leaders Chat
 */
export async function setLeadersChatTypingStatus(
  userId: string,
  userName: string,
  isTyping: boolean
): Promise<void> {
  const typingRef = doc(db, getLeadersChatTypingPath(userId));

  await setDoc(typingRef, {
    isTyping,
    userId,
    userName,
    timestamp: serverTimestamp(),
  });
}

/**
 * Subscribe to unread count for Leaders Chat
 */
export function subscribeToLeadersChatUnreadCount(
  userId: string,
  callback: (count: number) => void
): () => void {
  const metaRef = doc(db, getUserLeadersChatMetadataPath(userId), 'info');

  return onSnapshot(metaRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback(0);
      return;
    }
    const data = snapshot.data() as LeadersChatUserMetadata;
    callback(data.isMuted ? 0 : data.unreadCount || 0);
  });
}

// ==================== Read Status ====================

/**
 * Mark all Leaders Chat messages as read for a user
 */
export async function markLeadersChatRead(userId: string): Promise<void> {
  const metaRef = doc(db, getUserLeadersChatMetadataPath(userId), 'info');

  await setDoc(
    metaRef,
    {
      unreadCount: 0,
      lastReadAt: serverTimestamp(),
    },
    { merge: true }
  );
}

// ==================== User Metadata (Promotion/Demotion) ====================

/**
 * Initialize Leaders Chat metadata for a user (called on promotion to leader/admin).
 * Sets joinedAt to current timestamp so they only see messages from now forward.
 */
export async function initLeadersChatMetadata(userId: string): Promise<void> {
  const metaRef = doc(db, getUserLeadersChatMetadataPath(userId), 'info');
  const existing = await getDoc(metaRef);

  // Don't overwrite if already exists (e.g., re-promotion)
  if (existing.exists()) return;

  await setDoc(metaRef, {
    unreadCount: 0,
    lastReadAt: null,
    joinedAt: serverTimestamp(),
    isMuted: false,
  });
}

/**
 * Remove Leaders Chat metadata for a user (called on demotion to member).
 * Revokes access by removing their metadata document.
 */
export async function removeLeadersChatMetadata(userId: string): Promise<void> {
  const metaRef = doc(db, getUserLeadersChatMetadataPath(userId), 'info');
  try {
    await deleteDoc(metaRef);
  } catch (error) {
    // Document may not exist if user was never a leader
    console.warn('[LeadersChat] Could not remove metadata:', error);
  }
}

/**
 * Get the user's Leaders Chat metadata (for checking joinedAt)
 */
export async function getLeadersChatMetadata(
  userId: string
): Promise<LeadersChatUserMetadata | null> {
  const metaRef = doc(db, getUserLeadersChatMetadataPath(userId), 'info');
  const snap = await getDoc(metaRef);

  if (!snap.exists()) return null;
  return snap.data() as LeadersChatUserMetadata;
}

/**
 * Subscribe to user's Leaders Chat metadata (for joinedAt gating)
 */
export function subscribeToLeadersChatMetadata(
  userId: string,
  callback: (metadata: LeadersChatUserMetadata | null) => void
): () => void {
  const metaRef = doc(db, getUserLeadersChatMetadataPath(userId), 'info');

  return onSnapshot(metaRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }
    callback(snapshot.data() as LeadersChatUserMetadata);
  });
}

// ==================== Library Material Sharing ====================

/**
 * Post a library material to Leaders Chat.
 * Creates an encrypted message with embedded content metadata.
 */
export async function postMaterialToLeadersChat(
  file: LibraryFile,
  userId: string,
  userName: string,
  userRole: string,
  optionalMessage?: string
): Promise<string> {
  // Build embedded content metadata (stored as plaintext for rendering)
  const embeddedContent: EmbeddedContent = {
    type: 'library_material',
    fileId: file.id,
    title: file.title,
    fileType: file.type,
    url: file.url,
    ...(file.description && { description: file.description }),
    ...(file.authorName && { authorName: file.authorName }),
    ...(file.thumbnailUrl && { thumbnailUrl: file.thumbnailUrl }),
  };

  const messageText = optionalMessage?.trim() || `📚 New material shared: "${file.title}"`;

  return sendLeadersChatMessage(userId, userName, userRole, {
    text: messageText,
    embeddedContent,
  });
}

// ==================== Reactions Path Helper ====================

/**
 * Get the Firestore path for Leaders Chat message reactions
 */
export function getLeadersChatReactionsPath(messageId: string): string {
  return `${getLeadersChatMessagesPath()}/${messageId}/reactions`;
}

/**
 * Get the Firestore path for a Leaders Chat message document
 */
export function getLeadersChatMessageDocPath(messageId: string): string {
  return `${getLeadersChatMessagesPath()}/${messageId}`;
}
